import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all 21 language translations from JSON files
import enTranslation from './locales/en/translation.json';
import frTranslation from './locales/fr/translation.json';
import arTranslation from './locales/ar/translation.json';
import ptTranslation from './locales/pt/translation.json';
import swTranslation from './locales/sw/translation.json';
import zhTranslation from './locales/zh/translation.json';
import esTranslation from './locales/es/translation.json';
import hiTranslation from './locales/hi/translation.json';
import ruTranslation from './locales/ru/translation.json';
import jaTranslation from './locales/ja/translation.json';
import deTranslation from './locales/de/translation.json';
import koTranslation from './locales/ko/translation.json';
import trTranslation from './locales/tr/translation.json';
import itTranslation from './locales/it/translation.json';
import haTranslation from './locales/ha/translation.json';
import yoTranslation from './locales/yo/translation.json';
import igTranslation from './locales/ig/translation.json';
import pcmTranslation from './locales/pcm/translation.json';
import afTranslation from './locales/af/translation.json';
import amTranslation from './locales/am/translation.json';
import zuTranslation from './locales/zu/translation.json';

// Available languages configuration (21 languages)
export const languages = [
  // Major World Languages
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  // African Languages
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇪🇬', dir: 'rtl' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇦🇴', dir: 'ltr' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇹🇿', dir: 'ltr' },
  { code: 'ha', name: 'Hausa', nativeName: 'Hausa', flag: '🇳🇬', dir: 'ltr' },
  { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', dir: 'ltr' },
  { code: 'ig', name: 'Igbo', nativeName: 'Igbo', flag: '🇳🇬', dir: 'ltr' },
  { code: 'pcm', name: 'Pidgin', nativeName: 'Naija Pidgin', flag: '🇳🇬', dir: 'ltr' },
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', flag: '🇿🇦', dir: 'ltr' },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', dir: 'ltr' },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', flag: '🇿🇦', dir: 'ltr' }
];

// Resources object with all 21 translations
const resources = {
  en: { translation: enTranslation },
  fr: { translation: frTranslation },
  ar: { translation: arTranslation },
  pt: { translation: ptTranslation },
  sw: { translation: swTranslation },
  zh: { translation: zhTranslation },
  es: { translation: esTranslation },
  hi: { translation: hiTranslation },
  ru: { translation: ruTranslation },
  ja: { translation: jaTranslation },
  de: { translation: deTranslation },
  ko: { translation: koTranslation },
  tr: { translation: trTranslation },
  it: { translation: itTranslation },
  ha: { translation: haTranslation },
  yo: { translation: yoTranslation },
  ig: { translation: igTranslation },
  pcm: { translation: pcmTranslation },
  af: { translation: afTranslation },
  am: { translation: amTranslation },
  zu: { translation: zuTranslation }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'zh', 'es', 'hi', 'ru', 'ja', 'de', 'ko', 'tr', 'it', 'fr', 'ar', 'pt', 'sw', 'ha', 'yo', 'ig', 'pcm', 'af', 'am', 'zu'],
    
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'afriqoma_language'
    },
    
    interpolation: {
      escapeValue: false // React already escapes
    },
    
    react: {
      useSuspense: false
    }
  });

// Helper function to change language
export const changeLanguage = (langCode) => {
  i18n.changeLanguage(langCode);
  localStorage.setItem('afriqoma_language', langCode);
  
  // Update document direction for RTL languages
  const lang = languages.find(l => l.code === langCode);
  if (lang) {
    document.documentElement.dir = lang.dir;
    document.documentElement.lang = langCode;
  }
};

// Get current language info
export const getCurrentLanguage = () => {
  return languages.find(l => l.code === i18n.language) || languages[0];
};

export default i18n;
